import * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import AddButton from "./AddButton";
import RemoveButton from "./RemoveButton";
import { X, ExternalLink, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Reusable website/links input component with modern shadcn/ui design
 * @param {Array} websites - Array of website URL strings
 * @param {string} newWebsite - Current input value for new website
 * @param {Function} setNewWebsite - Setter for new website input: (value) => void
 * @param {Function} onAdd - Callback when website is added: () => void
 * @param {Function} onRemove - Callback when website is removed: (index) => void
 * @param {string} label - Label text (default: "Websites/Links")
 * @param {string} placeholder - Placeholder text (default: "https://yourwebsite.com")
 * @param {number} maxLength - Maximum length for input (default: 100)
 * @param {string} className - Additional CSS classes
 */
function WebsiteLinksInput({
  websites = [],
  newWebsite = "",
  setNewWebsite,
  onAdd,
  onRemove,
  label = "Websites/Links",
  placeholder = "https://yourwebsite.com",
  maxLength = 100,
  className = "",
}) {
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (onAdd && newWebsite.trim()) {
        onAdd();
      }
    }
  };

  const handleAdd = () => {
    if (onAdd && newWebsite.trim()) {
      onAdd();
      if (setNewWebsite) {
        setNewWebsite("");
      }
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            value={newWebsite}
            onChange={(e) => setNewWebsite && setNewWebsite(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            maxLength={maxLength}
            className="pl-9 placeholder:text-muted-foreground/60 placeholder:italic"
          />
        </div>
        <AddButton onClick={handleAdd} variant="secondary" size="default">
          Add
        </AddButton>
      </div>
      {websites.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {websites.map((site, idx) => (
            <Badge key={idx} variant="secondary" className="gap-2 pr-1">
              <a
                href={site}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3 text-primary" />
                <span className="max-w-[200px] truncate">{site}</span>
              </a>
              <RemoveButton
                onClick={() => onRemove && onRemove(idx)}
                ariaLabel={`Remove website ${idx + 1}`}
                size="icon"
                variant="ghost"
                className="h-5 w-5 hover:bg-destructive/10 hover:text-destructive"
                useXIcon={true}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default WebsiteLinksInput;
